const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
  "Accept": "*/*",
} as const;

export class NetworkError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown
  ) {
    super(message);
    this.name = "NetworkError";
  }
}

export type QueryParams = Record<string, string | number | boolean | undefined>;

interface RequestConfig extends RequestInit {
  headers?: HeadersInit;
  timeout?: number;
}

function extractErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  
  const obj = data as Record<string, unknown>;
  
  if (typeof obj.msg === 'string') return obj.msg;
  if (typeof obj.message === 'string') return obj.message;
  
  if (typeof obj.error === 'string') return obj.error;
  
  if (obj.data && typeof obj.data === 'object') {
    const nested = obj.data as Record<string, unknown>;
    if (typeof nested.error === 'string') return nested.error;
    if (nested.error && typeof nested.error === 'object') {
      const errorObj = nested.error as Record<string, unknown>;
      if (typeof errorObj.message === 'string') return errorObj.message;
    }
  }
  return undefined;
}

export async function request<T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  body?: unknown,
  query?: QueryParams,
  config: Omit<RequestConfig, "method" | "body"> = {}
): Promise<T> {
  let finalUrl = url.startsWith('http') 
    ? url 
    : `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;
  if (query) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });
    const queryString = params.toString();
    if (queryString) {
      finalUrl = `${finalUrl}${finalUrl.includes('?') ? '&' : '?'}${queryString}`;
    }
  }

  const {
    timeout = 30000,
    headers = {},
    credentials = "include",
    ...restConfig
  } = config;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(finalUrl, {
      ...restConfig,
      method,
      headers: {
        ...DEFAULT_HEADERS,
        ...headers,
      },
      credentials: credentials ?? "include",
      signal: controller.signal,
      body: body ? JSON.stringify(body) : undefined,
    });

    clearTimeout(timeoutId);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = extractErrorMessage(data);
      const defaultMessage = (response.status === 401 || response.status === 403)
        ? `Authentication failed with status ${response.status}`
        : `Request failed with status ${response.status}`;
      
      if (response.status === 401 || response.status === 403) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:unauthorized', {
            detail: { status: response.status, message: errorMessage }
          }));
        }
      }
      
      throw new NetworkError(
        errorMessage || defaultMessage,
        response.status,
        data
      );
    }

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === "AbortError") {
      throw new NetworkError("Request timeout. Please try again.", 0);
    }

    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new NetworkError("Network error. Please check your connection and try again.", 0);
    }

    if (error instanceof NetworkError) {
      throw error;
    }

    throw new NetworkError(
      error instanceof Error ? error.message : "An unexpected error occurred"
    );
  }
}

